<template>
  <div class="space-y-8">
    <h3 class="text-lg">Accounts Participating</h3>

    <div class="rounded shadow-xs">
      <div class="rounded shadow-lg bg-white overflow-hidden divide-y">
        <div class="px-4 py-3 lg:px-6 lg:py-4 space-y-3 lg:space-y-4">
          <div class="relative flex-grow">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg fill="currentColor" viewBox="0 0 20 20" class="w-5 h-5 text-gray-400">
                <path
                  fill-rule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817
                          4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <input
              type="text"
              class="w-full pl-10 pr-4 py-2 border rounded text-sm focus:outline-none focus:border-blue-700 focus:ring ring-blue-500 ring-opacity-50"
              placeholder="search accounts..."
              v-model="searchCriteria"
            />
          </div>
          <div>
            <span class="text-sm text-gray-600 inline-block mr-2">Filter</span>
            <label
              class="text-sm leading-6 px-3 py-2 rounded-l"
              :class="[filter.participants ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black']"
            >
              <input
                type="checkbox"
                v-model="filter.participants"
                class="appearance-none opacity-0"
              />
              <span>Participants</span>
            </label>
            <label
              class="text-sm leading-6 px-3 py-2"
              :class="[filter.providers ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black']"
            >
              <input type="checkbox" v-model="filter.providers" class="appearance-none opacity-0" />
              <span>Providers</span>
            </label>
            <label
              class="text-sm leading-6 px-3 py-2 rounded-r"
              :class="[filter.clients ? 'bg-blue-600 text-white' : 'bg-gray-300 text-black']"
            >
              <input type="checkbox" v-model="filter.clients" class="appearance-none opacity-0" />
              <span>Clients</span>
            </label>
          </div>
        </div>
        <div class="bg-gray-100 px-4 py-3 lg:px-6 lg:py-4">
          <span class="inline-block text-sm font-medium text-gray-600">Account name</span>
        </div>
        <ul class="divide-y">
          <li v-if="loading" class="bg-white px-4 py-3 lg:px-6 lg:py-4 text-sm">
            <div class="text-center">
              <Loader />
            </div>
          </li>
          <li
            v-for="accountRelation in accountRelations"
            :key="accountRelation.uuid"
            class="bg-white px-4 py-3 lg:px-6 lg:py-4 text-sm"
          >
            <div class="flex items-center space-x-4 lg:space-x-6">
              <div class="flex-1 space-y-2">
                <div>
                  {{ accountRelation.name }}
                </div>
                <BaseBadge
                  :silver="accountRelation.type === 'client'"
                  :orange="accountRelation.type === 'provider'"
                >
                  {{ accountRelation.type }}
                </BaseBadge>
              </div>
              <div class="flex-none">
                <BaseButton
                  v-if="
                    accountRelation.related_account_uuid &&
                    accountParticipatingIds.indexOf(accountRelation.related_account_uuid) === -1
                  "
                  primary
                  :disabled="active"
                  @click="addProjectAccount(accountRelation.related_account_uuid)"
                >
                  Share
                </BaseButton>
                <BaseButton
                  v-else-if="
                    accountRelation.related_account_uuid &&
                    accountParticipatingIds.indexOf(accountRelation.related_account_uuid) !== -1
                  "
                  danger
                  :disabled="active"
                  @click="removeProjectAccount(accountRelation.related_account_uuid)"
                >
                  Remove
                </BaseButton>
                <span v-else-if="!accountRelation.related_account_uuid"> Missing account </span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccountsParticipating',

  props: {
    projectId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      active: false,
      searchCriteria: '',
      filter: {
        providers: true,
        clients: false,
        participants: true,
      },
    };
  },

  computed: {
    project() {
      return this.$store.state.entities.projects.byId[this.projectId];
    },
    accountParticipatingIds() {
      return this.$store.state.ui.projectAccounts.ids;
    },
    accountsParticipating() {
      return this.$store.getters['ui/projectAccounts/projectAccounts'];
    },
    accountRelations() {
      return this.$store.getters['ui/accountAccountRelations/accountRelations']
        .filter((accountRelation) => {
          if (
            this.filter.participants &&
            this.accountParticipatingIds.indexOf(accountRelation.related_account_uuid) > -1
          ) {
            return true;
          }

          if (this.filter.providers && accountRelation.type === 'provider') {
            return true;
          }

          if (this.filter.clients && accountRelation.type === 'client') {
            return true;
          }

          return false;
        })
        .filter((accountRelation) => {
          const searchCriteria = this.searchCriteria.split(' ');

          return searchCriteria.every(
            (criteria) => accountRelation.name.toLowerCase().indexOf(criteria.toLowerCase()) !== -1
          );
        });
    },
  },

  created() {
    this.init();
  },

  destroyed() {
    this.$store.commit('ui/projectAccounts/setIds', []);
    this.$store.commit('ui/accountAccountRelations/setIds', []);
  },

  methods: {
    async init() {
      this.loading = true;
      await this.loadProjectAccounts();
      await this.loadAccountRelations();
      this.loading = false;
    },
    async loadProjectAccounts() {
      try {
        await this.$store.dispatch({
          type: 'ui/projectAccounts/load',
          id: this.projectId,
        });
      } catch (error) {
        //
      }
    },
    async loadAccountRelations() {
      try {
        await this.$store.dispatch({
          type: 'ui/accountAccountRelations/load',
          id: this.project.accountUuid,
          params: {
            sort: 'name',
          },
        });
      } catch (error) {
        //
      }
    },
    addProjectAccount(accountId) {
      this.updateProjectAccounts({
        accountIds: [...this.accountParticipatingIds, accountId],
      });
    },
    removeProjectAccount(accountId) {
      this.updateProjectAccounts({
        accountIds: [...this.accountParticipatingIds.filter((id) => id !== accountId)],
      });
    },
    async updateProjectAccounts(body) {
      try {
        this.active = true;
        await this.$store.dispatch({
          type: 'ui/projectAccounts/update',
          id: this.projectId,
          body,
        });
      } catch (error) {
        //
      } finally {
        this.active = false;
      }
    },
  },
};
</script>
